/**
 * AssetUploadModal — upload generic (imagine sau fișier) cu E2E encryption.
 *
 * La upload: client citește fișierul → encryptSym cu DEK nou → wrapKey
 * pentru public_key-ul fiecărui membru al tenantului → POST /assets cu
 * blob + dek_wraps. La click pe asset din galerie → callback onInsert cu
 * AssetMeta; caller-ul (NoteEditor) decide nodul (AssetImage vs
 * FileAttachment) în funcție de `mime_type`.
 *
 * Galeria afișează toate asseturile notiței: imaginile ca miniaturi, restul
 * ca iconiță fișier cu filename + MIME.
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { assetsApi, type AssetMeta } from '@/api/assets';
import { ApiError, errorMessage } from '@/api/client';
import { bytesToBase64, encryptSym, generateDEK } from '@/crypto/sodium';
import Modal from '@/components/Modal';
import { FileIcon } from '@/features/notes/icons';
import { wrapDekForNoteShares } from '@/features/notes/noteShareWraps';
import { useAuthStore } from '@/stores/auth';
import i18n from '@/i18n';

export type UploadKind = 'image' | 'file';

interface Props {
  noteId: string;
  tenantId: string;
  kind: UploadKind;
  onClose: () => void;
  onInsert: (asset: AssetMeta) => void;
}

const MAX_BYTES = 10 * 1024 * 1024;

function isImageMime(mime: string): boolean {
  return mime.toLowerCase().startsWith('image/');
}

export default function AssetUploadModal({
  noteId,
  tenantId,
  kind,
  onClose,
  onInsert,
}: Props) {
  const keyPair = useAuthStore((s) => s.keyPair);
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const { t } = useTranslation();

  const assets = useQuery({
    queryKey: ['note-assets', noteId],
    queryFn: () => assetsApi.list(noteId),
  });


  const upload = useMutation({
    mutationFn: async (file: File) => {
      if (!keyPair) throw new Error(t('notes_ui.assets.no_key'));
      if (kind === 'image' && !isImageMime(file.type || '')) {
        throw new Error(
          t('notes_ui.assets.unsupported_image_type', { type: file.type || t('notes_ui.assets.unknown_type') }),
        );
      }
      if (file.size > MAX_BYTES) {
        throw new Error(
          t('notes_ui.assets.too_large', { size: (file.size / 1024 / 1024).toFixed(1) }),
        );
      }
      const bytes = new Uint8Array(await file.arrayBuffer());
      const dek = await generateDEK();
      const { ciphertext, nonce } = await encryptSym(bytes, dek);

      // Destinatarii sunt userii cu share pe notiță, nu toți membrii
      // tenantului — backend-ul respinge wraps pentru useri fără share.
      const { dek_wraps } = await wrapDekForNoteShares({
        noteId,
        tenantId,
        dek,
        keyPair,
        userId: user?.id,
      }).finally(() => dek.fill(0)); // DEK-ul nu mai e necesar după împachetare

      return assetsApi.upload(noteId, {
        filename: file.name,
        mime_type: file.type || 'application/octet-stream',
        byte_size: file.size,
        ciphertext: bytesToBase64(ciphertext),
        nonce: bytesToBase64(nonce),
        dek_wraps,
      });
    },
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['note-assets', noteId] });
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    onError: (err) => {
      setError(errorMessage(err));
    },
  });

  const handleFile = async (file: File) => {
    setUploading(true);
    try {
      await upload.mutateAsync(file);
    } finally {
      setUploading(false);
    }
  };

  const accept = kind === 'image' ? 'image/*' : '*/*';
  const dropHint =
    kind === 'image'
      ? t('notes_ui.assets.drop_image')
      : t('notes_ui.assets.drop_file');
  const dropHintSuffix =
    kind === 'image'
      ? t('notes_ui.assets.drop_image_hint')
      : t('notes_ui.assets.drop_file_hint');
  const titleText =
    kind === 'image' ? t('notes_ui.assets.title_image') : t('notes_ui.assets.title_file');

  const title = <h2 className="display-title-sm">{titleText}</h2>;

  return (
    <Modal open onClose={onClose} title={title} maxWidth="max-w-3xl">
      <div className="space-y-4">
        {/* Dropzone */}
        <label
          onDragOver={(e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
          }}
          onDrop={(e) => {
            e.preventDefault();
            const f = e.dataTransfer.files?.[0];
            if (f) void handleFile(f);
          }}
          className="block border-2 border-dashed border-[var(--border-strong)] rounded-md p-8 text-center cursor-pointer hover:bg-[var(--bg-hover)] transition-colors"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleFile(f);
            }}
            disabled={uploading}
          />
          <div className="text-sm text-[var(--text-muted)]">
            {uploading ? (
              <span className="text-brand-700 dark:text-brand-300">{t('notes_ui.assets.uploading')}</span>
            ) : (
              <>
                <span className="font-medium">{dropHint}</span>{' '}
                <span className="text-[var(--text-muted)]">{t('notes_ui.assets.or_click')}</span>
                <div className="text-[10px] text-[var(--text-dim)] mt-1">
                  {dropHintSuffix}
                </div>
              </>
            )}
          </div>
        </label>

        {error && (
          <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded px-3 py-2">
            {error}
          </div>
        )}

        {/* Galerie — toate asseturile notiței */}
        <div>
          <h3 className="display-title-xs mb-2">
            {t('notes_ui.assets.gallery_title')}{' '}
            {assets.data && (
              <span className="normal-case text-[var(--text-dim)]">
                ({assets.data.length})
              </span>
            )}
          </h3>
          {assets.isLoading && (
            <p className="text-xs text-[var(--text-muted)]">{t('common.loading')}</p>
          )}
          {assets.data && assets.data.length === 0 && (
            <p className="text-xs text-[var(--text-muted)]">
              {t('notes_ui.assets.empty')}
            </p>
          )}
          {assets.data && assets.data.length > 0 && (
            <div className="grid grid-cols-3 gap-3">
              {assets.data.map((a) => (
                <DeletableAsset
                  key={a.id}
                  asset={a}
                  onDeleted={() => {
                    queryClient.invalidateQueries({ queryKey: ['note-assets', noteId] });
                    queryClient.invalidateQueries({ queryKey: ['note-assets-count', noteId] });
                  }}
                  onError={setError}
                >
                  {isImageMime(a.mime_type) ? (
                    <ImageThumb
                      asset={a}
                      onInsert={() => {
                        onInsert(a);
                        onClose();
                      }}
                    />
                  ) : (
                    <FileCard
                      asset={a}
                      onInsert={() => {
                        onInsert(a);
                        onClose();
                      }}
                    />
                  )}
                </DeletableAsset>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ==========================================================
// DeletableAsset — card + ștergere în doi pași (soft-delete pe server)
// ==========================================================

function DeletableAsset({
  asset,
  onDeleted,
  onError,
  children,
}: {
  asset: AssetMeta;
  onDeleted: () => void;
  onError: (message: string) => void;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const remove = useMutation({
    mutationFn: () => assetsApi.delete(asset.id),
    onSuccess: onDeleted,
    onError: (err) => {
      setConfirming(false);
      onError(err instanceof ApiError ? err.detail : t('notes.asset_delete_failed'));
    },
  });

  return (
    <div className="relative flex flex-col gap-1">
      {children}
      {confirming ? (
        <div className="text-[10px] text-[var(--text-muted)] space-y-1">
          <p>{t('notes.asset_delete_confirm')}</p>
          <div className="flex gap-2">
            <button
              type="button"
              className="text-red-600 dark:text-red-400 font-medium hover:underline disabled:opacity-50"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
            >
              {t('notes.asset_delete_yes')}
            </button>
            <button
              type="button"
              className="hover:underline"
              onClick={() => setConfirming(false)}
              disabled={remove.isPending}
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="self-start text-[10px] text-[var(--text-dim)] hover:text-red-600 dark:hover:text-red-400"
          onClick={() => setConfirming(true)}
        >
          {t('notes.asset_delete')}
        </button>
      )}
    </div>
  );
}

// ==========================================================
// ImageThumb — decrypt + miniatură imagine
// ==========================================================

function ImageThumb({
  asset,
  onInsert,
}: {
  asset: AssetMeta;
  onInsert: () => void;
}) {
  const keyPair = useAuthStore((s) => s.keyPair);
  const [url, setUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (!keyPair) return;
    let objectUrl: string | null = null;
    let cancelled = false;
    (async () => {
      try {
        const { decryptWithWrappedKey } = await import('@/crypto/sodium');
        const detail = await assetsApi.get(asset.id);
        const plain = await decryptWithWrappedKey(detail, keyPair);
        if (cancelled) return;
        const blob = new Blob([new Uint8Array(plain)], { type: detail.mime_type });
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : i18n.t('notes_ui.assets.generic_error'));
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [asset.id, keyPair]);

  return (
    <button
      type="button"
      onClick={onInsert}
      className="group relative border border-[var(--border-strong)] rounded-md overflow-hidden hover:ring-2 hover:ring-brand-500 transition-all"
      title={t('notes_ui.assets.insert', { filename: asset.filename })}
    >
      <div className="aspect-square bg-[var(--bg-surface)] flex items-center justify-center">
        {err ? (
          <span className="text-[10px] text-red-600 dark:text-red-400 p-2">{err}</span>
        ) : url ? (
          <img src={url} alt={asset.filename} className="w-full h-full object-cover" />
        ) : (
          <span className="text-[10px] text-[var(--text-dim)]">...</span>
        )}
      </div>
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="text-[10px] text-white truncate">{asset.filename}</div>
        <div className="text-[9px] text-white/70">
          {(asset.byte_size / 1024).toFixed(0)} KB
        </div>
      </div>
    </button>
  );
}

// ==========================================================
// FileCard — placeholder iconiță pentru fișiere non-image
// ==========================================================

function FileCard({
  asset,
  onInsert,
}: {
  asset: AssetMeta;
  onInsert: () => void;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onInsert}
      className="group border border-[var(--border-strong)] rounded-md overflow-hidden hover:ring-2 hover:ring-brand-500 transition-all text-left"
      title={t('notes_ui.assets.insert', { filename: asset.filename })}
    >
      <div className="aspect-square bg-[var(--bg-surface)] flex items-center justify-center text-[var(--text-dim)]">
        <FileIcon className="w-10 h-10" />
      </div>
      <div className="px-2 py-1.5 bg-[var(--bg-elevated)] border-t border-[var(--border-strong)]">
        <div className="text-[11px] font-medium truncate text-[var(--text)]">
          {asset.filename}
        </div>
        <div className="text-[9px] text-[var(--text-muted)] font-mono truncate">
          {asset.mime_type} · {(asset.byte_size / 1024).toFixed(0)} KB
        </div>
      </div>
    </button>
  );
}
